import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RemoveIcon from '@mui/icons-material/Remove';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CountdownTimer from './CountdownTimer';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import { useCalEventEdit } from '../useCalEventEdit';
import {
    _formatDue,
    cardSx,
    titlebarSx,
    catSx,
    bodySx,
    labelSx,
    descSx,
    rowSx,
    iconSx,
    metaSx,
    dividerSx,
    dismissRowSx,
    dismissLabelSx,
} from './CalendarCardHelper';

import './CalendarCard.css';
// import '../../theme/jTheme.css';

/**
 * CalendarCard — pure painter for a single CalEvent.
 * No logic — all state derived from props.
 * Draggable via dragHandleProps from CalendarCard2 (JxFormDraggableBase) — drag handle is the
 * titlebar only, cursor:grab lives there too (not on the whole card).
 * Root Box has class "cal-card" — CalendarCard.css defines --j-* vars locally
 * since CalendarCard2 renders via createPortal to document.body, outside
 * .j-workspace where the global jTheme.css vars are scoped.
 * Uses tooltipSxElevated — tooltips must render above the floating card's zIndex.
 *
 * @param {object} props
 * @param {CalEventObj} props.event
 * @param {object} props.dragHandleProps - spread onto titlebar for drag support
 * @param {boolean} props.ringing - true when reminder is active
 * @param {function} props.onMinimize - called when minimize is clicked
 * @param {function} props.onDismiss - called when dismiss is clicked
 * @param {function} props.onEditReminder - called when the "due in" row is clicked
 * @param {boolean} props.selected - true when card is selected
 */
export default function CalendarCard({ event, dragHandleProps, ringing, onMinimize, onDismiss, onEditReminder, selected }) {
    const { editingRk } = useCalEventEdit();

    if (!event) return null;

    const editBlocked = editingRk !== null && editingRk !== event.rk;
    const danger = event.isRinging();

    return (
        <Box className="cal-card" sx={cardSx(ringing, selected, danger)}>
            {/* Titlebar — drag handle (entire bar) + cat badge + minimize */}
            <Box sx={titlebarSx} {...dragHandleProps}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <DragIndicatorIcon sx={{ fontSize: 13, color: 'var(--j-text)' }} />
                    <Typography sx={catSx}>{event.cat || 'Event'}</Typography>
                </Box>
                {onMinimize && (
                    <Tooltip title="Minimaliseren" componentsProps={tooltipSxElevated}>
                        <IconButton
                            size="small"
                            onClick={onMinimize}
                            sx={{
                                p: '1px',
                                color: 'var(--j-text)',
                            }}
                        >
                            <RemoveIcon sx={{ fontSize: 11 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Body */}
            <Box sx={bodySx}>
                {/* Label */}
                <Tooltip title={event.label} placement="top" arrow componentsProps={tooltipSxElevated}>
                    <Typography sx={labelSx} noWrap>
                        {event.label}
                    </Typography>
                </Tooltip>

                {/* Desc — 1 regel ellipsis, tooltip voor volledige tekst */}
                {event.desc && (
                    <Tooltip title={event.desc} placement="bottom" arrow componentsProps={tooltipSxElevated}>
                        <Typography sx={descSx} noWrap>
                            {event.desc}
                        </Typography>
                    </Tooltip>
                )}

                {/* Date */}
                <Box sx={rowSx}>
                    <CalendarMonthIcon sx={iconSx} />
                    <Typography sx={metaSx}>
                        {event.date}
                        {event.time ? ` · ${event.time}` : ''}
                    </Typography>
                </Box>

                {/* Due in / countdown — click opens reminder editor, blocked while another event is being edited */}
                {event.ems > 0 && (
                    <>
                        <Box sx={dividerSx} />
                        <Tooltip
                            title={editBlocked ? 'Eerst andere wijziging opslaan of annuleren' : 'Klik hier om te wijzigen'}
                            componentsProps={tooltipSxElevated}
                        >
                            <Box
                                sx={{
                                    ...rowSx,
                                    justifyContent: 'space-between',
                                    cursor: editBlocked ? 'not-allowed' : onEditReminder ? 'pointer' : 'inherit',
                                    opacity: editBlocked ? 0.5 : 1,
                                }}
                                onClick={editBlocked ? undefined : onEditReminder}
                            >
                                <Box sx={rowSx}>
                                    <NotificationsIcon
                                        sx={{
                                            ...iconSx,
                                            color: danger
                                                ? 'var(--j-danger)'
                                                : ringing
                                                  ? 'var(--j-accent)'
                                                  : event.msUntil() <= 7200000
                                                    ? '#85B7EB'
                                                    : 'var(--j-muted)',
                                        }}
                                    />
                                    {/* Countdown als binnen 2 uur, anders normale tekst */}
                                    {event.msUntil() <= 7200000 ? (
                                        <CountdownTimer ems={event.ems} ringing={ringing} />
                                    ) : (
                                        <Typography sx={{ ...metaSx, color: ringing ? 'var(--j-accent)' : 'var(--j-muted)' }}>
                                            {event.isOverDue() ? 'overdue' : _formatDue(event.msUntil())}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={rowSx}>
                                    <EditOutlinedIcon sx={{ fontSize: 11, color: 'var(--j-muted)' }} />
                                </Box>
                            </Box>
                        </Tooltip>

                        {/* Dismiss — verwijdert event permanent uit de agenda */}
                        {onDismiss && (
                            <Tooltip title="Dit verwijdert dit item uit je agenda" componentsProps={tooltipSxElevated}>
                                <Box sx={dismissRowSx}>
                                    <EventBusyIcon sx={{ fontSize: 11, color: 'var(--j-muted)' }} />
                                    <Box>
                                        <Typography sx={dismissLabelSx} onClick={onDismiss}>
                                            Delete
                                        </Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
